/**
 * SeoHead
 * Mantiene las etiquetas SEO del <head> (título, descripción, canonical,
 * hreflang, Open Graph y Twitter) alineadas con la ruta actual. No renderiza
 * nada; debe montarse dentro del Router, junto a `LanguageSync`.
 *
 * Es una SPA de Create React App, así que las etiquetas se gestionan en el
 * cliente (Google renderiza JavaScript). Sin dependencias nuevas: la lógica
 * vive en `seoMeta` (qué texto), `seo` (qué URLs) y `headTags` (escritura en
 * el DOM). El idioma se toma de la URL, igual que `LanguageSync`, para no
 * depender del orden de efectos con i18next.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from './languages';
import { OG_LOCALES, buildSeoLinks } from './seo';
import { resolveSeoMeta } from './seoMeta';
import { localizeContent } from './localizeContent';
import { applyHeadTags } from './headTags';

/**
 * @returns {null} Componente sin salida visual
 */
const SeoHead = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const { language, canonical, alternates } = buildSeoLinks(pathname);
    const t = i18n.getFixedT(language, 'seo');
    const lc = (value) => localizeContent(value, language);
    const { title, description } = resolveSeoMeta(pathname, { t, lc });

    applyHeadTags({
      title,
      description,
      canonical,
      alternates,
      ogLocale: OG_LOCALES[language],
      ogLocaleAlternates: SUPPORTED_LANGUAGES.filter((l) => l !== language).map((l) => OG_LOCALES[l]),
    });
  }, [pathname, i18n]);

  return null;
};

export default SeoHead;
